import React, { Dispatch, SetStateAction } from "react";
import { DeviceUserGroupTable } from "./DeviceUserGroupTable";
import { Layout } from "views/Main/Layouts";
import { NoResultsIndicatorAnimation } from "elements";
import { ProtectedLink, ProtectedRoute } from "TsRoleGuards";
import { infiniteNestLink } from "polyrouter";
import {
  getColumns,
  getSelectedWithoutHeader,
  TableSearchEdit
} from "collections/Table";
import { useTranslation } from "utilities/i18n";
import { TABLE_COLUMN_STATE, TABLES } from "store/userMeta";
import { useSelector } from "react-redux";
import { State } from "store";
import { CreateGroupModal } from "routes/Manage/DeviceUsers/Shared/Modals";
import { UserGroup } from "generated/graphql";
import { DeleteGroupsModal } from "../Delete/DeleteGroupsModal";

export const DeviceUserGroupListView: React.FC<{
  userGroups: Array<
    Omit<UserGroup, "users" | "policies"> & { user_count: number } & {
      policies: Array<{ id: string; priority: string }>;
    }
  >;
  userCanEdit: boolean;
  selectedItems: Array<string>;
  setSelectedItems: Dispatch<SetStateAction<string[]>>;
  loading: boolean;
  tenantId: string;
}> = ({
  userGroups,
  userCanEdit,
  selectedItems,
  setSelectedItems,
  loading,
  tenantId
}) => {
  const { t } = useTranslation();

  const { NAME: TABLE_NAME, COLUMNS } = TABLES.DEVICE_USER_GROUPS;
  const {
    [TABLE_COLUMN_STATE.SHOWN]: shownColumns,
    [TABLE_COLUMN_STATE.HIDDEN]: hiddenColumns
  } = useSelector((state: State) => {
    return state.userMeta?.tables[TABLE_NAME] ?? {};
  });

  const columnTranslations = {
    [COLUMNS.NAME]: t("NAME"),
    [COLUMNS.USER_COUNT]: t("DEVICE_USER_COUNT"),
    [COLUMNS.CREATED_AT]: t("DATE_CREATED"),
    [COLUMNS.LAST_MODIFIED]: t("LAST_MODIFIED"),
    [COLUMNS.GROUP_TYPE]: t("GROUP_CREATION"),
    [COLUMNS.POLICY_PRIORITY]: t("POLICY_PRIORITY")
  };

  const columnOrder = [
    COLUMNS.NAME,
    COLUMNS.USER_COUNT,
    COLUMNS.CREATED_AT,
    COLUMNS.LAST_MODIFIED,
    COLUMNS.GROUP_TYPE,
    COLUMNS.POLICY_PRIORITY
  ];

  const shown = getColumns(shownColumns, columnTranslations);
  shown.sort((a, b) => columnOrder.indexOf(a.key) - columnOrder.indexOf(b.key));
  const hidden = getColumns(hiddenColumns, columnTranslations);

  const userGroupCount = userGroups.length;
  const selectedGroups = getSelectedWithoutHeader(selectedItems);

  const { Content } = Layout;
  return (
    <Content className="small-sidebar-nav-padding device-user-inventory">
      {!loading && userGroupCount === 0 ? (
        <NoResultsIndicatorAnimation title={t("NO_DEVICE_USER_GROUPS")}>
          <ProtectedLink
            nest
            to={{ pathname: "/group/create" }}
            className="btn btn-primary"
            requirement="update:usergroup"
          >
            {t("ADD_NEW")}
          </ProtectedLink>
        </NoResultsIndicatorAnimation>
      ) : (
        <>
          <div className="table-heading">
            <span className="metric">{userGroupCount}</span>
            <span className="title ml-5">
              {t("DEVICE_USER_GROUP", { count: userGroupCount })}
            </span>
          </div>
          <div className="grouping mb-15 table-actions">
            <ProtectedLink
              nest
              to={{ pathname: "/group/create" }}
              className="btn btn-primary"
              requirement="update:usergroup"
            >
              {t("ADD_NEW")}
            </ProtectedLink>
            <ProtectedLink
              to={infiniteNestLink("/group/delete")}
              disabled={!selectedItems?.length}
              className={`btn ${selectedItems?.length ? "" : "disabled"}`}
              requirement="delete:usergroup"
            >
              {t("DELETE")}
            </ProtectedLink>
            <TableSearchEdit
              edit={{
                tableName: TABLE_NAME,
                shownColumns: shown,
                hiddenColumns: hidden
              }}
            />
          </div>
          <DeviceUserGroupTable
            userGroups={userGroups}
            userCanEdit={userCanEdit}
            selectedItems={selectedItems}
            setSelectedItems={setSelectedItems}
            loading={loading}
            shown={shown}
          />
        </>
      )}
      <ProtectedRoute nest path="/group/create" requirement="grant:role">
        <CreateGroupModal
          tenantId={tenantId}
          deviceUsers={[]}
          urlEndPath="/group/create"
        />
      </ProtectedRoute>
      <ProtectedRoute nest path="/group/delete" requirement="grant:role">
        <DeleteGroupsModal tenantId={tenantId} groups={selectedGroups} />
      </ProtectedRoute>
    </Content>
  );
};
